<i18n>
{
  "en": {
    "recommendations": "Menu banner recommendations",
    "add": "Add new recommendation",
    "deleteTitle": "Are you sure you want to delete this recommendation",
    "delete": "Delete",
    "createError": "There was an error creating the recommendation",
    "overlapError": "This recommendation overlaps with another",
    "defaultOverlapError": "There is already a default recommendation that overlaps",
    "editError": "There was an error editing the recommendation",
    "deleteError": "There was an error deleting the recommendation",
    "requestError": "There was an error in the request",
    "deleteDescription": "Irreversible action",
    "noRecommendations": "No Recommendation available",
    "recommendationDefaultName" : "Recommendation",
    "createBannerSuccess": "Created \"{0}\" banner!",
    "editBannerSuccess": "Edited \"{0}\" banner!",
    "deleteBannerSuccess": "Deleted \"{0}\" banner!"
  },
  "es": {
    "recommendations": "Recomendaciones de menú de tipo banner",
    "add": "Añadir nueva recomendación",
    "deleteTitle": "¿Estás seguro de que quieres borrar esta recomendación?",
    "delete": "Borrar",
    "createError": "Ha habido un error creando la recomendación",
    "overlapError": "Esta recomendación se solapa con otra",
    "defaultOverlapError": "Ya existe otra recomendación por defecto que se solapa con esta",
    "editError": "Ha habido un error editando la recomendación",
    "deleteError": "Ha habido un error borrando la recomendación",
    "requestError": "Ha habido un error en la petición",
    "deleteDescription": "Acción irreversible",
    "noRecommendations": "No hay Recomendaciones disponibles",
    "recommendationDefaultName" : "Recomendación",
    "createBannerSuccess": "Creado el \"{0}\" banner!",
    "editBannerSuccess": "Editado el \"{0}\" banner!",
    "deleteBannerSuccess": "Eliminado el \"{0}\" banner!"
  }
}
</i18n>

<template>
  <v-container class="mb-10">
    <div class="d-flex flex-wrap">
      <recommendation-filter @menu="onMenu" @default="onDefault"
                             :menuList="menuList" :serviceList="serviceList"/>
    </div>
    <div class="d-flex justify-space-between">
      <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1">
        {{ $t('recommendations') }}
      </div>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="mr-2" v-on="on" v-bind="attrs" @click="onOpenCreateModal()">
            <v-icon color="grey" large>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('add') }}</span>
      </v-tooltip>
    </div>
    <div v-if="isEmpty"
         class="d-flex justify-space-around grey--text mt-5">
      <span>{{ $t('noRecommendations') }}</span>
    </div>
    <div v-if="!isLoading">
      <v-list v-for="recommendation in filteredRecommendationList" :key="recommendation.guid">
        <recommendation-card :bannerRecommendation="recommendation"
                             :menuName="menuNamesByGuid[recommendation.menu]"
                             :productName="productNamesByGuid[recommendation.product]"
                             @edit="onOpenEditModal(recommendation)"
                             @delete="onOpenDeleteModal(recommendation)"/>
      </v-list>
    </div>
    <dynameat-skeleton v-else skeleton-type="manualConfigList" :container="false"/>

    <dynameat-confirmation-modal :visible="deleteModalVisible" :title="$t('deleteTitle')"
                                 :description="$t('deleteDescription')" :confirm-text="$t('delete')"
                                 @confirm="onConfirmDeleteModal" @cancel="onCloseDeleteModal"/>

    <recommendation-create-edit-modal :visible="recommendationCreateEditModalVisible"
                                      @confirm-create="onConfirmCreateModal"
                                      @confirm-edit="onConfirmEditModal"
                                      :menuList="menuFiltered"
                                      :serviceList="serviceList"
                                      :edit-mode="modalEditMode"
                                      :recommendation="recommendationToEdit"
                                      :defaultRecommendationTitle="nextDefaultRecommendationTitle"
                                      @cancel="onCloseCreateEditModal"
    />
  </v-container>
</template>

<script>
import Vue from 'vue';

import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import Component from 'vue-class-component';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import { Watch } from 'vue-property-decorator';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import RecommendationCreateEditModal from './components/RecommendationCreateEditModal.vue';
import RecommendationFilter from './components/RecommendationFilter.vue';
import RecommendationCard from './components/RecommendationCard.vue';

@Component({
  components: { RecommendationCreateEditModal }
})
@Components({
  RecommendationFilter,
  RecommendationCard,
  DynameatConfirmationModal,
  DynameatSkeleton
})

@MapGetters(['menuBannerRecommendationList', 'menuList', 'serviceList', 'editableProductList',
  'isLoading', 'templateList'])
@MapActions(['menuListRequest', 'bannerRecommendationListRequest', 'deleteBannerRecommendation',
  'masterProductListRequest', 'setLoading', 'addNotification', 'createBannerRecommendation',
  'editBannerRecommendation', 'templateListRequest', 'addNotification'])
export default class MenuBannerRecommendation extends Vue {
  menuFilter = [];

  defaultFilter = 'all';

  recommendationToDelete = null;

  recommendationToEdit = null;

  recommendationCreateEditModalVisible = false;

  deleteModalVisible = false;

  modalEditMode = false;

  async created() {
    await this.loadRecommendationsData();
    await this.loadMenuEnabledProductListData();
    await this.loadMenuData();
    await this.templateListRequest();
  }

  async loadRecommendationsData() {
    try {
      this.setLoading(1);
      await this.bannerRecommendationListRequest(this.$route.params.pointOfSaleGuid);
    } catch {
      this.addNotification(createErrorNotification(this.$t('requestError')));
    } finally {
      this.setLoading(-1);
    }
  }

  async loadMenuEnabledProductListData() {
    try {
      await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
  }

  async loadMenuData() {
    try {
      await this.menuListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
  }

  get filteredRecommendationList() {
    let recommendationList = this.menuBannerRecommendationList;
    if (this.defaultFilter !== 'all') {
      recommendationList = recommendationList.filter((el) => el.activeByDefault === (this.defaultFilter === 'yes'));
    }
    if (this.menuFilter.length > 0) {
      recommendationList = recommendationList.filter((el) => this.menuFilter.includes(el.menu));
    }
    return recommendationList;
  }

  get menuNamesByGuid() {
    return this.menuList.reduce((acc, currentMenu) => {
      acc[currentMenu.guid] = currentMenu.title;
      return acc;
    }, {});
  }

  get productNamesByGuid() {
    return this.editableProductList.reduce((acc, currentProduct) => {
      acc[currentProduct.guid] = currentProduct.name;
      return acc;
    }, {});
  }

  get isEmpty() {
    return !this.isLoading && this.menuBannerRecommendationList.length === 0;
  }

  get menuFiltered() {
    return this.menuList.filter(this.isAllowedBannerRecommendation);
  }

  get nextDefaultRecommendationTitle() {
    const name = this.$t('recommendationDefaultName');
    const recommendations = this.filteredRecommendationList;
    let i = 0;
    let defaultTitle = '';
    do {
      i += 1;
      defaultTitle = name.concat(' ', i);
    } while (recommendations.some((recommendation) => recommendation.title === defaultTitle));
    return defaultTitle;
  }

  isAllowedBannerRecommendation(menu) {
    const menuTemplate = this.templateList.find((temp) => temp.guid === menu.template);
    if (menuTemplate) {
      return !menuTemplate.isOperationDisallowed('MANUAL_CONFIG', 'MENU_BANNER_RECOMMENDATION');
    }
    return false;
  }

  onOpenCreateModal() {
    this.recommendationToEdit = null;
    this.modalEditMode = false;
    this.recommendationCreateEditModalVisible = true;
  }

  onOpenEditModal(recommendation) {
    this.recommendationToEdit = Object.create(recommendation);
    this.modalEditMode = true;
    this.recommendationCreateEditModalVisible = true;
  }

  onCloseCreateEditModal() {
    this.recommendationToDelete = null;
    this.recommendationCreateEditModalVisible = false;
  }

  onOpenDeleteModal(recommendation) {
    this.recommendationToDelete = recommendation;
    this.deleteModalVisible = true;
  }

  onCloseDeleteModal() {
    this.recommendationToDelete = null;
    this.deleteModalVisible = false;
  }

  async onConfirmCreateModal(recommendation) {
    try {
      await this.createBannerRecommendation(recommendation);
      this.onCloseCreateEditModal();
      this.addNotification(createSuccessNotification(this.$t('createBannerSuccess', [recommendation.title]), null, 3000));
    } catch (err) {
      if (err.response?.status === 409) {
        if (recommendation.activeByDefault) this.addNotification(createErrorNotification(this.$t('defaultOverlapError'), null, 5000));
        else this.addNotification(createErrorNotification(this.$t('overlapError'), null, 5000));
      } else {
        this.addNotification(createErrorNotification(this.$t('createError'), null, 5000));
      }
    }
  }

  async onConfirmEditModal(recommendation) {
    try {
      await this.editBannerRecommendation(recommendation);
      this.onCloseCreateEditModal();
      this.addNotification(createSuccessNotification(this.$t('editBannerSuccess', [recommendation.title]), null, 3000));
    } catch (err) {
      if (err.response?.status === 409) {
        if (recommendation.activeByDefault) this.addNotification(createErrorNotification(this.$t('defaultOverlapError'), null, 5000));
        else this.addNotification(createErrorNotification(this.$t('overlapError'), null, 5000));
      } else {
        this.addNotification(createErrorNotification(this.$t('editError'), null, 5000));
      }
    }
  }

  async onConfirmDeleteModal() {
    this.deleteModalVisible = false;
    try {
      await this.setLoading(1);
      await this.deleteBannerRecommendation(this.recommendationToDelete);
      this.addNotification(createSuccessNotification(this.$t('deleteBannerSuccess', [this.recommendationToDelete.title])));
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('deleteError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  onMenu(array) {
    this.menuFilter = array;
  }

  onDefault(string) {
    this.defaultFilter = string;
  }

  clearOwnData() {
    this.menuFilter = [];
    this.defaultFilter = 'all';
    this.recommendationToDelete = null;
    this.recommendationToEdit = null;
    this.recommendationCreateEditModalVisible = false;
    this.deleteModalVisible = false;
    this.modalEditMode = false;
  }

  @Watch('$route')
  async onRouteChange(to, from) {
    if (to.params.pointOfSaleGuid !== from.params.pointOfSaleGuid) {
      this.clearOwnData();
      await this.loadRecommendationsData();
      await this.loadMenuEnabledProductListData();
      await this.loadMenuData();
    }
  }
}
</script>

<style scoped>

</style>
